/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { TrackedArray } from 'tracked-built-ins';
import { action, computed } from '@ember/object';
import { filter, setDiff, sort } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PageAccessLevel from '@linc-technologies/linced-common/enums/page-access-level';
import PageType from 'linced-app/enums/page-type';
import ReactionType from '@linc-technologies/linced-common/enums/reaction-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';
import config from 'linced-app/config/environment';
import moment from 'moment';

// restoreAndThrow puts a record back after a failed save or delete (undeleted,
// with relationship edits reverted), then rethrows.
function restoreAndThrow(record, err) {
  rollbackRecord(record);
  throw err;
}

export default class Pages extends Controller {
  queryParams = [
    {
      currentFilter: 'type',
    },
  ];

  // Services
  @service
  fetch;

  @service
  options;

  @service
  paperToaster;

  @service
  permissions;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  Jurisdiction = Jurisdiction;

  PageAccessLevel = PageAccessLevel;

  PageType = PageType;

  ReactionType = ReactionType;

  Scopes = Scopes;

  @tracked
  currentFilter = PageType.STUDENT;

  @tracked
  hasPosts = false;

  @action
  setHasPosts(value) {
    this.hasPosts = value;
  }

  // Methods
  @computed('model.pages.@each.type', 'currentFilter')
  get filteredPages() {
    return this.get('model.pages')
      .filter((item) => item.type === this.currentFilter)
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }

  @filter('filteredPages.@each.numPageSets', function (page) {
    return !page.numPageSets;
  })
  commonPages;

  @computed(
    'model.templates.@each.{type,isSickbay,isSuspensionPosts,isSuspensionLetters,isBehaviour}',
    'currentFilter',
    'session.scopes.[]',
  )
  get filteredTemplates() {
    let templates = this.get('model.templates')
      .filter((item) => item.type === this.currentFilter)
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    // Only linced managers and above can see sickbay templates
    if (this.permissions.meet(Scopes.LINCED_MANAGER)) {
      return templates;
    }
    return templates
      .filter((item) => item.isSickbay !== true)
      .filter((item) => item.isSuspensionPosts !== true)
      .filter((item) => item.isSuspensionLetters !== true)
      .filter((item) => item.isBehaviour !== true);
  }

  currentYear = moment().year();

  @computed('model.autogroups.[]', 'pageSetAutogroup')
  get filteredAutogroups() {
    let currentAutogroups = this.model.autogroups.filter(
      (item) => item.year === this.currentYear,
    );
    if (
      !isEmpty(this.pageSetAutogroup) &&
      this.get('pageSetAutogroup.year') !== this.currentYear
    ) {
      // Selected autogroup isn't from the current year - prepend it.
      currentAutogroups.unshift(this.pageSetAutogroup);
    }
    return currentAutogroups;
  }

  @tracked
  importInProgress = false;

  @tracked pageId = '';

  @tracked templateId = '';

  @tracked
  pageLabel = '';

  pageKey = '';

  pageVisibleToParents = false;

  pageVisibleToLearners = false;

  pageSiblingLinked = false;

  pageType = null;

  @tracked
  hasLearnerIcon = false;

  @action
  setHasLearnerIcon(value) {
    this.hasLearnerIcon = value;
  }

  @tracked
  hasAutogroup = false;

  @action
  setHasAutogroup(value) {
    this.hasAutogroup = value;
  }

  @tracked isSpecialUse = false;

  @tracked specialUseLabel = '';

  @tracked selectedPage = null;

  @tracked
  type = Object.freeze({ value: 1, label: 'Student' });

  @action
  setType(value) {
    this.type = value;
  }

  // Assessments config
  @tracked
  hasAssessments = false;

  @tracked
  sortingAssessments = false;

  // TrackedArray, not a plain array: assessments backs a DragSortList
  // (sortDragEnd mutates it in place with native splice), and add/remove
  // actions also mutate it in place rather than reassigning.
  @tracked
  assessments = new TrackedArray();

  // Plain getter, not @setDiff: a classic macro doesn't reliably observe
  // a TrackedArray's autotracked reads.
  get remainingAssessments() {
    return this.model.assessments.filter(
      (assessment) => !this.assessments.includes(assessment),
    );
  }

  // Plain getter, not @computed: same TrackedArray reasoning.
  get hasMultipleAssessments() {
    return this.hasAssessments && this.assessments.length > 1;
  }

  // Assessment removal from a page
  @tracked
  removingAssessmentDialogOpen = false;

  @tracked
  removedAssessment = null;

  @tracked
  convertAssessmentLinkedPosts = true;

  @action
  setConvertAssessmentLinkedPosts(value) {
    this.convertAssessmentLinkedPosts = value;
  }

  @tracked
  deletingAssessmentPosts = [];

  @computed('session.scopes.[]')
  get isLincedManager() {
    return this.permissions.meet(Scopes.LINCED_MANAGER);
  }

  get isEditable() {
    return !isEmpty(this.pageId);
  }

  // Plain array, not TrackedArray: add/remove actions rebuild-and-reassign
  // (no drag-sort/in-place-mutation consumer, unlike assessments/
  // goalSubjects), so @setDiff correctly observes it unchanged.
  @tracked tagCategories = [];

  @setDiff('model.tagCategories', 'tagCategories')
  remainingTagCategories;

  @tracked hasGoals = false;

  @tracked
  hasGoalsAsTags = false;

  @action
  setHasGoalsAsTags(value) {
    this.hasGoalsAsTags = value;
  }

  // TrackedArray, not a plain array: same reasoning as assessments above -
  // backs a DragSortList and add/remove actions mutate it in place.
  @tracked goalSubjects = new TrackedArray();

  // Plain getter, not @setDiff: same TrackedArray reasoning as
  // remainingAssessments above.
  get remainingGoalSubjects() {
    return this.model.goalSubjects.filter(
      (subject) => !this.goalSubjects.includes(subject),
    );
  }

  get hasMultipleGoalSubjects() {
    return this.goalSubjects.length > 1;
  }

  @tracked sortingGoalSubjects = false;

  @tracked hasOTJAssessment = false;

  // Page configuration
  @tracked
  icon = '';

  @action
  setIcon(value) {
    this.icon = value;
  }

  @tracked
  label = '';

  @action
  setLabel(value) {
    this.label = value;
  }

  @tracked
  title = '';

  @action
  setTitle(value) {
    this.title = value;
  }

  @tracked
  description = '';

  @action
  setDescription(value) {
    this.description = value;
  }

  @tracked
  caregiverDescription = '';

  @action
  setCaregiverDescription(value) {
    this.caregiverDescription = value;
  }

  @tracked
  learnerDescription = '';

  @action
  setLearnerDescription(value) {
    this.learnerDescription = value;
  }

  @tracked
  postTemplate = '';

  @action
  setPostTemplate(value) {
    this.postTemplate = value;
  }

  @tracked
  reactionType = Object.freeze({ value: 1, label: 'None' });

  @action
  setReactionType(value) {
    this.reactionType = value;
  }

  // Page visibility
  @tracked
  visibleInMenu = false;

  @action
  setVisibleInMenu(value) {
    this.visibleInMenu = value;
  }

  @tracked
  visibleOnContent = false;

  @action
  setVisibleOnContent(value) {
    this.visibleOnContent = value;
  }

  @tracked
  visibleOnStudentProfile = false;

  @action
  setVisibleOnStudentProfile(value) {
    this.visibleOnStudentProfile = value;
  }

  @tracked
  visibleToParents = false;

  @action
  setVisibleToParents(value) {
    this.visibleToParents = value;
  }

  // Page authorisation / permissions
  @tracked
  staffCanPublish = false;

  @action
  setStaffCanPublish(value) {
    this.staffCanPublish = value;
  }

  @tracked
  staffCanEdit = false;

  @action
  setStaffCanEdit(value) {
    this.staffCanEdit = value;
  }

  @tracked
  caregiversCanPost = false;

  @action
  setCaregiversCanPost(value) {
    this.caregiversCanPost = value;
  }

  @tracked
  learnersCanPost = false;

  @action
  setLearnersCanPost(value) {
    this.learnersCanPost = value;
  }

  @tracked
  canComment = false;

  @action
  setCanComment(value) {
    this.canComment = value;
  }

  // Is this a special page
  @tracked
  isSickbay = false;

  @action
  setIsSickbay(value) {
    this.isSickbay = value;
  }

  @tracked
  isSuspensionPosts = false;

  @action
  setIsSuspensionPosts(value) {
    this.isSuspensionPosts = value;
  }

  @tracked
  isSuspensionLetters = false;

  @action
  setIsSuspensionLetters(value) {
    this.isSuspensionLetters = value;
  }

  @tracked
  isBehaviour = false;

  @action
  setIsBehaviour(value) {
    this.isBehaviour = value;
  }

  @tracked
  isAttendance = false;

  @action
  setIsAttendance(value) {
    this.isAttendance = value;
  }

  @tracked
  pageDialogOpen = false;

  @tracked
  saveInProgress = false;

  @tracked
  deletePageDialogOpen = false;

  @tracked
  deleteInProgress = false;

  @tracked
  errorMessage = '';

  pageProperties = Object.freeze([
    'hasAssessments',
    'hasPosts',
    'hasGoals',
    'hasEffort',
    'hasGoalsAsTags',
    'hasOTJAssessment',
    'icon',
    'label',
    'title',
    'description',
    'caregiverDescription',
    'learnerDescription',
    'postTemplate',
    'reactionType',
    'visibleInMenu',
    'visibleOnContent',
    'visibleOnStudentProfile',
    'visibleToParents',
    'visibleToLearners',
    'staffCanPublish',
    'staffCanEdit',
    'caregiversCanPost',
    'learnersCanPost',
    'canComment',
    'hasLearnerIcon',
    'hasAutogroup',
    'accessLevel',
    'isSickbay',
    'isAttendance',
    'isSuspensionPosts',
    'isSuspensionLetters',
    'isBehaviour',
  ]);

  @tracked
  pageSetDialogOpen = false;

  @tracked
  pageSetSaveInProgress = false;

  @tracked
  pageSet = null;

  @tracked
  pageSetLabel = '';

  @action
  setPageSetLabel(value) {
    this.pageSetLabel = value;
  }

  @tracked
  pageSetAutogroup = null;

  @action
  setPageSetAutogroup(value) {
    this.pageSetAutogroup = value;
  }

  @tracked
  selectedPageSet = null;

  // True while editPage waits for the page's pageSets relationship.
  @tracked
  pageSetsLoading = false;

  // Changes on each editPage and resetForm, so a late pageSets load can see
  // that its edit has closed or been replaced.
  #pageSetsLoad = 0;

  @action
  setSelectedPageSet(value) {
    this.selectedPageSet = value;
  }

  pageTypes = PageType.values;

  reactionTypes = ReactionType.values;

  @tracked
  templateDialogOpen = false;

  @tracked
  sortDialogOpen = false;

  pageSort = Object.freeze(['sequence', 'label']);

  @sort('filteredPages', 'pageSort')
  sortedFilteredPages;

  @computed('filteredPages.@each.sequence')
  get anyPageHasSequence() {
    return !!this.filteredPages.find((page) => {
      return page.get('sequence') > 0;
    });
  }

  editorOptions = Object.freeze(defaultTinyMCEConfig());

  // Page Media
  @tracked
  pageMedia = null;

  @tracked
  pageCaregiverMedia = null;

  @tracked
  pageLearnerMedia = null;

  @tracked
  stagedMedia = null;

  @tracked
  stagedCaregiverMedia = null;

  @tracked
  stagedLearnerMedia = null;

  // Learner Page Access Level
  @tracked accessLevel = PageAccessLevel.STANDARD;

  get hasRestrictedAccess() {
    // Disable page visibility flags for caregiver or learners for non-standard pages.
    return this.accessLevel !== PageAccessLevel.STANDARD;
  }

  accessLevelNoteMap = Object.freeze({
    [PageAccessLevel.STANDARD]:
      ' - only visible to staff members who have standard, sensitive, or confidential access. No access to staff members with restricted access.',
    [PageAccessLevel.SENSITIVE]:
      ' - only visible to staff members who have sensitive or confidential access.',
    [PageAccessLevel.CONFIDENTIAL]:
      ' - only visible to staff members who have confidential level access.',
  });

  @tracked
  deletePageSetDialogOpen;

  @tracked
  visibleToLearners;

  @action
  setVisibleToLearners(value) {
    this.visibleToLearners = value;
  }

  @tracked storedAccessLevel;

  @tracked hasEffort;

  @computed(
    'filteredPages.@each.{isSickbay,isAttendance,isSuspensionPosts,isSuspensionLetters,isBehaviour}',
    'isSickbay',
    'isSuspensionPosts',
    'isSuspensionLetters',
    'isBehaviour',
    'isAttendance',
  )
  get disabledSwitches() {
    const {
      isSickbay,
      isSuspensionPosts,
      isSuspensionLetters,
      isBehaviour,
      isAttendance,
      filteredPages,
    } = this;

    const findPageByProperty = (property) =>
      findBy(filteredPages, property, true);

    const sickbayPage = findPageByProperty('isSickbay');
    const sickbaySwitchDisabled =
      (sickbayPage && !isSickbay) ||
      isSuspensionPosts ||
      isSuspensionLetters ||
      isAttendance ||
      isBehaviour;

    const attendancePage = findPageByProperty('isAttendance');
    const attendanceSwitchDisabled =
      (attendancePage && !isAttendance) ||
      isSuspensionPosts ||
      isSuspensionLetters ||
      isSickbay ||
      isBehaviour;

    const suspensionPostsPage = findPageByProperty('isSuspensionPosts');
    const suspensionPostsSwitchDisabled =
      (suspensionPostsPage && !isSuspensionPosts) ||
      isSuspensionLetters ||
      isAttendance ||
      isSickbay;

    const suspensionLettersPage = findPageByProperty('isSuspensionLetters');
    const suspensionLettersSwitchDisabled =
      (suspensionLettersPage && !isSuspensionLetters) ||
      isSuspensionPosts ||
      isAttendance ||
      isSickbay;

    const behaviourPage = findPageByProperty('isBehaviour');
    const behaviourSwitchDisabled =
      (behaviourPage && !isBehaviour) || isSickbay || isAttendance;

    return {
      sickbaySwitchDisabled,
      suspensionPostsSwitchDisabled,
      suspensionLettersSwitchDisabled,
      behaviourSwitchDisabled,
      attendanceSwitchDisabled,
    };
  }

  @action
  addPage() {
    let type = PageType.values.find(
      (item) => item.value === this.currentFilter,
    );
    this.hasPosts = this.currentFilter !== PageType.STUDENT;
    this.type = type;
    this.errorMessage = '';
    this.pageDialogOpen = true;
  }

  @action
  togglePageDialog() {
    this.errorMessage = '';
    this.toggleProperty('pageDialogOpen');
    if (!this.pageDialogOpen) {
      this.resetForm();
    }
  }

  @action
  openSortDialog() {
    this.sortDialogOpen = true;
  }

  @action
  closeSortDialog() {
    this.sortDialogOpen = false;
  }

  // drag-drop sorting (shared by both the assessments and goalSubjects
  // DragSortLists)
  @action
  sortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    const item = sourceList[sourceIndex];

    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);
  }

  @action
  setAccessBasedFlags(accessLevel) {
    this.accessLevel = accessLevel;
    if (accessLevel !== PageAccessLevel.STANDARD) {
      // If page access level is not Standard
      // Set following flags related to page visibility for caregiver or learners to false
      this.visibleOnStudentProfile = false;
      this.visibleToParents = false;
      this.visibleOnContent = false;
      this.visibleToLearners = false;
      this.caregiversCanPost = false;
      this.learnersCanPost = false;
    }
  }

  @action
  editPage(id) {
    this.store.findRecord('posts/page', id).then((page) => {
      this.setProperties(page.getProperties(this.pageProperties));
      this.type = this.pageTypes.find(
        (item) => item.value === page.get('type'),
      );
      this.reactionType = this.reactionTypes.find(
        (item) => item.value === page.get('reactionType'),
      );
      this.goalSubjects = new TrackedArray(page.get('goalSubjects').slice());
      this.tagCategories = page.get('tagCategories').slice();
      this.assessments = new TrackedArray(page.get('assessments').slice());
      // pageSets is an async hasMany (a PromiseManyArray), which has no index
      // access; wait for the loaded records. Keep the selector and save
      // disabled until they load, and ignore the result if the dialog has
      // closed or another page is in edit.
      const load = ++this.#pageSetsLoad;
      this.pageSetsLoading = true;
      page
        .get('pageSets')
        .then((pageSets) => {
          if (load === this.#pageSetsLoad) {
            this.selectedPageSet = pageSets[0];
          }
        })
        .finally(() => {
          if (load === this.#pageSetsLoad) {
            this.pageSetsLoading = false;
          }
        });
      // storedAccessLevel is used for comparing existing access value with new assigned access value
      this.storedAccessLevel = page.get('accessLevel');
      this.isSpecialUse = page.get('isSpecialUse');
      this.specialUseLabel = page.get('specialUseLabel');
      this.selectedPage = page;

      // Page media
      let media = page.get('media');
      let caregiverMedia = page.get('caregiverMedia');
      let learnerMedia = page.get('learnerMedia');
      if (!isEmpty(media)) {
        media.then((media) => {
          this.pageMedia = media;
          this.stagedMedia = media;
        });
      }
      if (!isEmpty(caregiverMedia)) {
        caregiverMedia.then((caregiverMedia) => {
          this.pageCaregiverMedia = caregiverMedia;
          this.stagedCaregiverMedia = caregiverMedia;
        });
      }
      if (!isEmpty(learnerMedia)) {
        learnerMedia.then((learnerMedia) => {
          this.pageLearnerMedia = learnerMedia;
          this.stagedLearnerMedia = learnerMedia;
        });
      }
    });
    this.pageId = id;
    this.togglePageDialog();
  }

  @action
  savePage() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;
    let action = 'create';

    if (isEmpty(this.pageId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord(
          'posts/page',
          this.getProperties(this.pageProperties),
        );
        record.setProperties({
          schoolId: this.get('session.school'),
          authorId: this.get('session.personId'),
          type: this.get('type.value'),
          reactionType: this.get('reactionType.value'),
          goalSubjectIds: [...this.goalSubjects],
          tagCategoryIds: this.tagCategories,
          assessmentIds: [...this.assessments],
          pageTemplateId: this.templateId, // Empty unless importing a template.
          media: this.stagedMedia,
          caregiverMedia: this.stagedCaregiverMedia,
          learnerMedia: this.stagedLearnerMedia,
        });

        if (this.anyPageHasSequence) {
          // Pages are not in the default purely-alphabetical sort.
          // Put the new page at the end of the list by default.
          record.set('sequence', 999);
        }

        if (
          this.get('type.value') === PageType.STUDENT &&
          !isEmpty(this.selectedPageSet)
        ) {
          // pageSets is an async hasMany: its PromiseManyArray can't be
          // mutated, so assign the whole list.
          record.set('pageSetIds', [this.selectedPageSet]);
        }

        resolve(record);
      });
    } else {
      action = 'update';
      promise = new Promise((resolve) => {
        let page = this.store.peekRecord('posts/page', this.pageId);
        page.setProperties(this.getProperties(this.pageProperties));
        page.setProperties({
          type: this.get('type.value'),
          reactionType: this.get('reactionType.value'),
          goalSubjectIds: [...this.goalSubjects],
          tagCategoryIds: this.tagCategories,
          assessmentIds: [...this.assessments],
          media: this.stagedMedia,
          caregiverMedia: this.stagedCaregiverMedia,
          learnerMedia: this.stagedLearnerMedia,
        });

        if (this.get('type.value') === PageType.STUDENT) {
          // pageSets is an async hasMany: its PromiseManyArray can't be
          // mutated, so assign the whole list.
          page.set(
            'pageSetIds',
            isEmpty(this.selectedPageSet) ? [] : [this.selectedPageSet],
          );
        }

        resolve(page);
      });
    }

    promise.then((record) => {
      return this.cleanUpMedia(this.stagedMedia, this.pageMedia).then(
        (media) => {
          if (!isEmpty(media)) {
            record.set('media', media);
          }
          return record;
        },
      );
    });
    promise.then((record) => {
      return this.cleanUpMedia(
        this.stagedCaregiverMedia,
        this.pageCaregiverMedia,
      ).then((media) => {
        if (!isEmpty(media)) {
          record.set('caregiverMedia', media);
        }
        return record;
      });
    });
    promise.then((record) => {
      return this.cleanUpMedia(
        this.stagedLearnerMedia,
        this.pageLearnerMedia,
      ).then((media) => {
        if (!isEmpty(media)) {
          record.set('learnerMedia', media);
        }
        return record;
      });
    });

    promise.then((record) => {
      return record
        .save()
        .then(() => {
          if (action === 'create') {
            // Send an event to trigger the route to refresh the model
            this.router.refresh('admin.pages');
          }

          this.pageDialogOpen = false;
          this.resetForm();
        })
        .catch((err) => {
          this.handleError(err, action);
          // The form still holds the edits for a retry; rollbackAttributes()
          // alone would leave the goal subjects, assessments and page sets.
          rollbackRecord(record);
        })
        .then(() => {
          this.saveInProgress = false;
        });
    });
  }

  @action
  deletePage(id) {
    this.errorMessage = '';
    this.store.findRecord('posts/page', id).then((page) => {
      this.pageLabel = page.get('label');
    });
    this.pageId = id;
    this.deletePageDialogOpen = true;
  }

  @action
  confirmDelete(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let field = this.store.peekRecord('posts/page', this.pageId);

        // Delete record (locally + pushed to server).
        return field
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            field.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deletePageDialogOpen = false;
        this.resetForm();
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  @action
  toggleTemplateDialog() {
    this.toggleProperty('templateDialogOpen');
  }

  @action
  importTemplate(template) {
    let applyUrl = `${config.APP.apiHost}/posts/${config.APP.apiPath}/templates/pages/${template.id}/:apply`;
    let data = {
      schoolId: this.session.activeSchool,
      // TODO: add campusId when implemented
    };
    this.importInProgress = true;
    this.fetch.post(applyUrl, { data }).then((response) => {
      // Push the new record into the store to make it easier to handle.
      this.store.pushPayload('posts/page', response);
      // Set sequence to 999, so it drops to the end - as a new page would.
      let page = this.store.peekRecord('posts/page', response.page.id);
      if (this.anyPageHasSequence) {
        // Pages have been sorted; put the new one at the end.
        page.set('sequence', 999);
      } else {
        // Pages haven't been sorted; let it slot in alphabetically.
        page.set('sequence', 0);
      }
      page.save().then(() => {
        this.templateDialogOpen = false;
        this.importInProgress = false;
        this.model.pages.update();
        this.editPage(response.page.id);
      });
    });
  }

  @action
  addGoalSubject(subject) {
    this.goalSubjects.push(subject);
  }

  @action
  removeGoalSubject(subject) {
    let pos = this.goalSubjects.indexOf(subject);
    if (pos > -1) {
      this.goalSubjects.splice(pos, 1);
    }
  }

  @action
  addTagCategory(category) {
    this.tagCategories = [...this.tagCategories, category];
  }

  @action
  removeTagCategory(category) {
    this.tagCategories = this.tagCategories.filter((c) => c !== category);
  }

  @action
  addAssessment(assessment) {
    this.assessments.push(assessment);
  }

  @action
  removeAssessment(assessment) {
    // Find any linked posts for this assessment - page combination:
    this.store
      .query('posts/post', {
        assessmentId: assessment.id,
        pageId: this.selectedPage.id,
      })
      .then((posts) => {
        if (!isEmpty(posts)) {
          // Linked posts found - open a warning dialog
          this.removingAssessmentDialogOpen = true;
          this.removedAssessment = assessment;
          this.deletingAssessmentPosts = posts;
          return;
        }
        // No posts found - freely remove the assessment
        let pos = this.assessments.indexOf(assessment);
        if (pos > -1) {
          this.assessments.splice(pos, 1);
        }
      });
  }

  // Page Sets
  @action
  async openPageSetDialog(pageSet) {
    if (isEmpty(pageSet)) {
      this.pageSet = null;
      this.pageSetLabel = '';
      this.pageSetAutogroup = null;
      this.pageSetSaveInProgress = false;
    } else {
      this.pageSet = pageSet;
      this.pageSetLabel = pageSet.label;
      // A belongsTo takes a record, not the async proxy.
      this.pageSetAutogroup = await pageSet.autogroup;
      this.pageSetSaveInProgress = false;
    }

    this.pageSetDialogOpen = true;
  }

  @action
  closePageSetDialog() {
    this.pageSetDialogOpen = false;
  }

  @action
  savePageSet() {
    this.pageSetSaveInProgress = true;
    if (isEmpty(this.pageSet)) {
      let record = this.store.createRecord('posts/page-set', {
        schoolId: this.get('session.school'),
        authorId: this.get('session.personId'),
      });
      this.pageSet = record;
    }

    this.pageSet.setProperties({
      label: this.pageSetLabel,
      autogroupId: this.pageSetAutogroup,
    });

    this.pageSet
      .save()
      .then(() => {
        this.paperToaster.show(
          `Page Set "${this.pageSetLabel}" was saved successfully.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          },
        );

        this.pageSetDialogOpen = false;
      })
      .catch((err) => {
        this.paperToaster.show(
          `Failed to save Page Set "${this.pageSetLabel}.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          },
        );

        this.handleError(err);
      })
      .finally(() => {
        this.pageSetSaveInProgress = false;
      });
  }

  @action
  deletePageSet(pageSet) {
    this.errorMessage = '';
    this.pageSet = pageSet;
    this.deletePageSetDialogOpen = true;
  }

  @action
  confirmDeletePageSet(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        this.pageSet
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            this.pageSet.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deletePageSetDialogOpen = false;
        this.pageSet = null;
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  @action
  toggleHasAssessments() {
    this.toggleProperty('hasAssessments');
    if (this.hasAssessments) {
      // If page has assessments, set access level to default
      this.accessLevel = PageAccessLevel.STANDARD;
    }
  }

  // Page media upload handling.
  // onUploadFinished sets staged media to the newly created media record.
  @action
  onUploadFinished(media) {
    // TODO: for each change, store a history of uploaded media that can be
    //     removed on save or exit to keep our system tidy.
    this.stagedMedia = media;
  }

  // onUploadRemoved clears the staged media.
  @action
  onUploadRemoved() {
    this.stagedMedia = null;
  }

  @action
  onCaregiverUploadFinished(caregiverMedia) {
    this.stagedCaregiverMedia = caregiverMedia;
  }

  @action
  onCaregiverUploadRemoved() {
    this.stagedCaregiverMedia = null;
  }

  @action
  onLearnerUploadFinished(learnerMedia) {
    this.stagedLearnerMedia = learnerMedia;
  }

  @action
  onLearnerUploadRemoved() {
    this.stagedLearnerMedia = null;
  }

  // noOp provides a no op function for components that require it.
  @action
  noOp() {}

  @action
  closeRemoveAssessmentDialog() {
    this.removingAssessmentDialogOpen = false;
    this.removedAssessment = null;
    this.deletingAssessmentPosts = [];
  }

  @action
  confirmAssessmentRemoval() {
    let promises = [];
    // Update/delete the posts
    // A post that fails to save or delete is put back as the server has it.
    this.deletingAssessmentPosts.forEach((post) => {
      if (this.convertAssessmentLinkedPosts) {
        post.set('assessmentId', null);
        promises.push(post.save().catch((err) => restoreAndThrow(post, err)));
        return;
      }
      promises.push(
        post.destroyRecord().catch((err) => restoreAndThrow(post, err)),
      );
    });

    let removedFrom;
    return Promise.all(promises)
      .then(() => {
        // Remove the assessment from page and save the page in case a user
        // forgets to do it manually, so there is a lesser chance of a mistake
        let pos = this.assessments.indexOf(this.removedAssessment);
        if (pos > -1) {
          this.assessments.splice(pos, 1);
          removedFrom = pos;
        }
        this.selectedPage.set('assessmentIds', [...this.assessments]);
        return this.selectedPage.save().catch((err) => {
          // Put the assessment back on the page, and in the form.
          rollbackRecord(this.selectedPage);
          if (removedFrom !== undefined) {
            this.assessments.splice(removedFrom, 0, this.removedAssessment);
          }
          throw err;
        });
      })
      .then(() => {
        this.toaster.success('Assessment removed from page successfully.');
        this.closeRemoveAssessmentDialog();
        this.selectedPage.reload();
      })
      .catch(() => {
        this.toaster.error('Failed to remove assessment from page.');
      });
  }

  // cleanUpMedia checks to see if the media id is different, and if so,
  // destroys the previous piece of media.
  //
  // @param {Object|undefined} stagedMedia - contains the staged media to check against.
  // @param {Object|undefined} pageMedia - contains the existing page media to check against.
  // @return {Object|undefined} media - the staged media.
  cleanUpMedia(stagedMedia, pageMedia) {
    if (isEmpty(pageMedia)) {
      return Promise.resolve(stagedMedia);
    }
    if (isEmpty(stagedMedia) || pageMedia.get('id') !== stagedMedia.get('id')) {
      return pageMedia.destroyRecord().then(() => {
        return Promise.resolve(stagedMedia);
      });
    }

    return Promise.resolve(stagedMedia);
  }

  handleError(err) {
    if (!isEmpty(err.errors.msg)) {
      this.errorMessage = err.errors.msg;
    } else if (!isEmpty(err.errors[0])) {
      let error = err.errors[0];
      if (!isEmpty(error.detail)) {
        this.errorMessage = error.detail;
      }
    } else {
      this.errorMessage = err.message;
    }
  }

  setErrorMessage(code, action) {
    var message = 'An unknown error occurred.  Please try again.';
    const codes = {
      400: 'Sorry, your request was invalid.  Please check all details below, and try again.',
      403: {
        create:
          'Sorry, you are not authorised to create a new user.  Please check that you are logged in as the correct user, and try again.',
        update:
          'Sorry, you are not authorised to edit this user.  Please check that you are logged in as the correct user, and try again.',
        delete:
          'Sorry, you are not authorised to delete this user.  Please check that you are logged in as the correct user, and try again.',
      },
      409: 'A user already exists with that username.  Please double-check the details, or find and update the existing user.',
    };

    if (!isEmpty(codes[code][action])) {
      message = codes[code][action];
    } else if (!isEmpty(codes[code])) {
      message = codes[code];
    }

    this.errorMessage = message;
  }

  resetForm() {
    this.selectedPage = null;
    this.pageId = '';
    this.templateId = '';
    this.type = PageType.values.find(
      (item) => item.value === this.currentFilter,
    );
    this.hasAssessments = false;
    this.hasPosts = false;
    this.hasGoals = false;
    this.hasGoalsAsTags = false;
    this.hasOTJAssessment = false;
    this.goalSubjects = new TrackedArray();
    this.tagCategories = [];
    this.assessments = new TrackedArray();
    this.hasEffort = false;
    this.icon = '';
    this.label = '';
    this.pageMedia = null;
    this.pageCaregiverMedia = null;
    this.pageLearnerMedia = null;
    this.stagedMedia = null;
    this.stagedCaregiverMedia = null;
    this.stagedLearnerMedia = null;
    this.title = '';
    this.description = '';
    this.caregiverDescription = '';
    this.learnerDescription = '';
    this.postTemplate = '';
    this.reactionType = { value: 1, label: 'None' };
    this.visibleInMenu = false;
    this.visibleOnContent = false;
    this.visibleOnStudentProfile = false;
    this.visibleToParents = false;
    this.visibleToLearners = false;
    this.staffCanPublish = false;
    this.staffCanEdit = false;
    this.caregiversCanPost = false;
    this.learnersCanPost = false;
    this.canComment = false;
    this.selectedPageSet = null;
    this.#pageSetsLoad++;
    this.pageSetsLoading = false;
    this.sortingAssessments = false;
    this.sortingGoalSubjects = false;
    this.isSpecialUse = false;
    this.specialUseLabel = '';
    this.isSickbay = false;
    this.isAttendance = false;
    this.isSuspensionPosts = false;
    this.isSuspensionLetters = false;
    this.isBehaviour = false;
  }
}
